import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { Share2, Download, Link2, Loader2, Pencil, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { ContactModal } from "@/components/ContactModal";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function Board({ readOnly = false }) {
  const { id } = useParams();
  const ctx = useSession();
  const [remote, setRemote] = useState(null);
  const [brief, setBrief] = useState(null);
  const [writing, setWriting] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const boardRef = useRef(null);

  const s = readOnly ? remote : ctx.session;

  useEffect(() => {
    if (readOnly) api.get(`/sessions/${id}`).then(({ data }) => setRemote(data)).catch(() => setRemote(false));
  }, [readOnly, id]);

  useEffect(() => {
    if (!s || brief !== null) return;
    if (s.brief) return setBrief(s.brief);
    if (readOnly) return setBrief("");
    setWriting(true);
    api.post(`/sessions/${s.id}/brief`).then(({ data }) => setBrief(data.brief)).catch(() => setBrief("")).finally(() => setWriting(false));
  }, [s, brief, readOnly]);

  if (s === false) return <div className="min-h-screen flex items-center justify-center text-neutral-400">This board doesn't exist.</div>;
  if (!s) return <div className="min-h-screen" />;

  const styles = Array.from(new Set(s.selected_styles.map((x) => x.style)));
  const rooms = s.taste_list.reduce((acc, t) => ((acc[t.room] = acc[t.room] || []).push(t), acc), {});
  const size = s.area_sqft ? `${s.area_sqft} sq ft` : `${s.bedrooms ?? 0} bedrooms · ${s.halls ?? 0} halls`;
  const shareUrl = `${window.location.origin}/b/${s.id}`;

  const onSave = () => (s.contact ? setShareOpen(true) : setContactOpen(true));
  const copy = async () => { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };
  const download = async () => {
    setSaving(true);
    try {
      const url = await toPng(boardRef.current, { cacheBust: true, pixelRatio: 1.5, backgroundColor: "#ffffff" });
      const a = document.createElement("a");
      a.href = url; a.download = `interiorsmoodboard-${(s.project_name || "my-space").replace(/\s+/g, "-").toLowerCase()}.png`; a.click();
    } catch {
      toast.error("Couldn't render the image. Use the link instead.");
    }
    setSaving(false);
  };

  return (
    <main className="max-w-6xl mx-auto px-5 sm:px-10 pt-10 pb-32">
      {readOnly && (
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="font-display text-2xl tracking-wide">interiorsmoodboard</Link>
          <Link to="/" data-testid="public-board-cta" className="btn-ink h-10 px-5 text-xs">Make your own board</Link>
        </div>
      )}

      <div ref={boardRef} data-testid="board-canvas" className="relative bg-white p-6 sm:p-12 rounded-[2rem] border border-neutral-200 overflow-hidden">
        <div className="pointer-events-none select-none absolute -right-10 top-1/2 -translate-y-1/2 rotate-90 origin-center watermark text-[9px] sm:text-xs tracking-[0.4em] uppercase text-neutral-300 whitespace-nowrap">Made on interiorsmoodboard</div>

        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="eyebrow">{s.project_type} · {size}</p>
            <h1 data-testid="board-title" className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight mt-3">
              {s.project_name || "My space"}<span className="italic">, understood.</span>
            </h1>
            {s.compliment?.headline && <p data-testid="board-compliment" className="text-neutral-500 mt-3 italic font-display text-xl">“{s.compliment.headline}”</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {styles.map((st) => <span key={st} data-testid={`board-style-${st.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-full border border-neutral-900 px-3 h-8 inline-flex items-center text-xs">{st}</span>)}
          </div>
        </header>

        <section className="mt-12">
          <p className="eyebrow mb-4">Spaces that moved you</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {s.selected_styles.map((x, i) => (
              <img key={x.id} src={x.url} alt={x.title} crossOrigin="anonymous" className={`w-full object-cover rounded-xl ${i === 0 ? "col-span-2 row-span-2 h-full" : "aspect-square"}`} />
            ))}
          </div>
        </section>

        {s.chosen_palette && (
          <section className="mt-12" data-testid="board-palette">
            <p className="eyebrow mb-4">Palette · {s.chosen_palette.name}</p>
            <div className="flex h-20 rounded-2xl overflow-hidden">
              {s.chosen_palette.colors.map((c) => <div key={c} style={{ background: c }} className="flex-1 relative"><span className="absolute bottom-2 left-2 font-mono text-[10px] text-white mix-blend-difference">{c}</span></div>)}
            </div>
          </section>
        )}

        <section className="mt-12 grid md:grid-cols-2 gap-10">
          <div>
            <p className="eyebrow mb-4">In their words</p>
            <dl className="space-y-4">
              {Object.entries(s.answers).map(([k, v]) => (
                <div key={k} data-testid={`board-answer-${k}`}>
                  <dt className="text-[11px] uppercase tracking-wider text-neutral-400">{LABELS[k] || k}</dt>
                  <dd className="text-sm text-neutral-800 mt-1 leading-relaxed">{Array.isArray(v) ? v.join(", ") : v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div>
            <p className="eyebrow mb-4">Designer's brief</p>
            {writing ? (
              <p data-testid="brief-loading" className="font-display italic text-xl text-neutral-400">Writing your brief…</p>
            ) : (
              <p data-testid="board-brief" className="font-display text-xl sm:text-2xl font-light leading-relaxed whitespace-pre-line">{brief}</p>
            )}
          </div>
        </section>

        <section className="mt-14">
          <p className="eyebrow mb-6">Taste list · by room</p>
          {Object.keys(rooms).length === 0 && <p className="text-sm text-neutral-400">Nothing saved yet.</p>}
          <div className="space-y-10">
            {Object.entries(rooms).map(([room, items]) => (
              <div key={room} data-testid={`board-room-${room.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="font-display text-2xl font-light">{room}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3 mt-4">
                  {items.map((t) => (
                    <figure key={t.image_id}>
                      <img src={t.url} alt={t.title} crossOrigin="anonymous" className="w-full aspect-[4/5] object-cover rounded-xl" />
                      <figcaption className="text-xs text-neutral-600 mt-2">{t.title}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <footer className="mt-14 pt-6 border-t border-neutral-200 flex items-center justify-between text-xs text-neutral-400">
          <div className="flex flex-col gap-1">
            <span className="watermark text-neutral-900 text-base">Made on interiorsmoodboard</span>
            <span data-testid="board-footer-credit">by Lussomora · made by prosperabuzz.com</span>
          </div>
          <span>{new Date(s.updated_at).toLocaleDateString()}</span>
        </footer>
      </div>

      {!readOnly && (
        <div className="fixed bottom-0 inset-x-0 z-30 pointer-events-none">
          <div className="max-w-3xl mx-auto px-4 pb-6 flex justify-center gap-3">
            <Link to="/furniture" data-testid="board-edit-button" className="btn-ghost h-14 pointer-events-auto shadow-2xl bg-white"><Pencil size={16} /> Keep adding</Link>
            <button data-testid="board-save-share-button" onClick={onSave} className="btn-ink h-14 px-8 pointer-events-auto shadow-2xl"><Share2 size={16} /> Save & share</button>
          </div>
        </div>
      )}

      <ContactModal open={contactOpen} sessionId={s.id} onClose={() => setContactOpen(false)} onDone={async () => { setContactOpen(false); await ctx.refresh(); setShareOpen(true); }} />

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-modal" className="max-w-md rounded-3xl p-10 border-0">
          <p className="eyebrow">Your board is ready{s.contact ? `, ${s.contact.name.split(" ")[0]}` : ""}</p>
          <h3 className="font-display text-3xl sm:text-4xl font-light mt-3 leading-tight">Share it with anyone who should see it.</h3>
          <div className="mt-8 space-y-3">
            <button data-testid="share-copy-link-button" onClick={copy} className="btn-ghost w-full justify-start"><Link2 size={16} /> Copy link</button>
            <a data-testid="share-whatsapp-button" href={`https://wa.me/?text=${encodeURIComponent(`My interior taste board, made on interiorsmoodboard: ${shareUrl}`)}`} target="_blank" rel="noreferrer" className="btn-ghost w-full justify-start"><MessageCircle size={16} /> Send on WhatsApp</a>
            <button data-testid="share-download-button" onClick={download} disabled={saving} className="btn-ink w-full justify-start">{saving ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download as image</button>
          </div>
          <p className="text-[11px] text-neutral-400 mt-6 break-all font-mono">{shareUrl}</p>
        </DialogContent>
      </Dialog>
    </main>
  );
}

const LABELS = { liked: "What drew them in", people: "Who lives here", decision_makers: "Who has a say", feel: "Should feel", avoid: "Not this", budget: "Budget", timeline: "Timeline" };
